import { SetStateAction } from "react";

import { Anchor, Toast, LoadingSpinner } from "@newsela/angelou";

import {
  ADMIN_CONSOLE_CONTENT_PROMOTION_PATHNAME,
  PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE,
} from "@/components/ContentPromotions/constants";
import ContentPreviewContainer from "@/components/ContentPromotions/ContentPreviewContainer";
import { ContentPromotionForm } from "@/types/models/ContentPromotionForm.interface";
import ContentPromotionFormLinks from "@/yourContent/PromotedAtYourVenuePage/ContentPromotionFormLinks";
import { SubjectProductAbbreviation } from "@/components/ContentPromotions/types/SubjectProduct.enum";
import formatGradeRangeString from "@/chiron/PlatformAlignment/utils/formatGradeRangeString";
import { ContentPromotionFormResponse } from "@/types/models/ContentPromotionFormResponse.interface";
import { ContentPromotionFormQuestion } from "@/types/models/ContentPromotionFormQuestion.interface";
import globalToasterRef from "@/base/globalToasterRef";
import { ContentPromotionVenue } from "@/components/ContentPromotions/types/ContentPromotionVenue.enum";
import useLazyContentPromotionFormPost from "@/components/ContentPromotions/hooks/useLazyContentPromotionFormPost";
import { UserRoleEnum } from "@/users/getHighestRoleLevel/getHighestRoleLevel.constants";
import NoContentReturned from "../NoContentReturned";
import BannerToPublishContentPromotionForms from "../BannerToPublishContentPromotionForms";
import YourContentNav from "../YourContentNav";
import {
  errorToastCss,
  innerPageContainerCss,
  pageLinkCss,
  promotedContentContainerCss,
} from "./PromotedAtYourVenuePage.styles";
import { PREVIEW_SUBTITLE } from "./constants";
import { TabSelectorForContentPromotionVenue } from "./types";
import {
  generatePageTitle,
  getGradeRangeTextForSelectedForm,
  getSubjectProductLabelForSelectedForm,
} from "./utils";

interface PromotedAtYourVenueProps {
  venue: ContentPromotionVenue;
  selector: TabSelectorForContentPromotionVenue;
  relevantForms: ContentPromotionForm[] | undefined;
  selectedForm: ContentPromotionForm | undefined;
  fetchContentPromotionForms: () => void;
  urlPathname: PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE;
  isBannerVisibleToPublishForm: boolean;
  selectedFormResponses: ContentPromotionFormResponse[] | undefined;
  selectedFormQuestions: ContentPromotionFormQuestion[] | undefined;
  setIsLoadingRelevantForms: React.Dispatch<SetStateAction<boolean>>;
  isLoadingFormResponses: boolean;
  isLoadingRelevantForms: boolean;
  mostSeniorUserRole: UserRoleEnum | undefined;
}

export const PromotedAtYourVenuePage = ({
  mostSeniorUserRole,
  venue,
  fetchContentPromotionForms,
  relevantForms,
  selectedForm,
  selector,
  urlPathname,
  isBannerVisibleToPublishForm,
  selectedFormResponses,
  selectedFormQuestions,
  isLoadingRelevantForms,
  isLoadingFormResponses,
  setIsLoadingRelevantForms,
}: PromotedAtYourVenueProps) => {
  const [publishContentPromotionForm] = useLazyContentPromotionFormPost({
    form_id: selectedForm?.id,
    is_published: true,
  });

  const generateFormLinks = (
    relevantForms: ContentPromotionForm[] | undefined,
  ) => {
    return relevantForms
      ?.sort((formA, formB) => formA.grade_minimum - formB.grade_minimum) // sort by grade_minimum
      .sort((formA, formB) =>
        formA.product_slug.localeCompare(formB.product_slug),
      ) // then sort alphabetically by subject
      .map((form) => {
        const gradeRange = {
          min_grade: form.grade_minimum,
          max_grade: form.grade_maximum,
        };

        const subjectText = SubjectProductAbbreviation[form.product_slug];
        const gradeGradeRangeText = formatGradeRangeString(gradeRange, true);

        return {
          href: `${urlPathname}/?form_id=${form.id}`,
          text: `${subjectText} ${gradeGradeRangeText}`,
        };
      });
  };

  const selectedFormRedirectUrl = selectedForm?.id
    ? `${ADMIN_CONSOLE_CONTENT_PROMOTION_PATHNAME}/form/${selectedForm?.id}?question_number=1`
    : `${ADMIN_CONSOLE_CONTENT_PROMOTION_PATHNAME}`;

  const requiredQuestionsAreMissingResponses = () => {
    if (selectedFormResponses?.length === 0) return true;

    const requiredQuestions = selectedFormQuestions?.filter(
      (question: ContentPromotionFormQuestion) => question.is_required,
    );

    const requiredQuestionsHaveResponses = requiredQuestions?.some(
      (question: ContentPromotionFormQuestion) => {
        const questionHasResponse = selectedFormResponses?.some((response) => {
          return (
            response.platform_alignment_question.id === question.id &&
            response.response.length > 0
          );
        });

        return !questionHasResponse;
      },
    );

    return requiredQuestionsHaveResponses;
  };

  const handleSelectionToPublishContentPromotionForm = async () => {
    try {
      if (requiredQuestionsAreMissingResponses()) {
        displayToast(
          <span css={errorToastCss}>
            <p>
              One or more required questions are missing, please review and
              complete the form
            </p>
            <Anchor
              href={selectedFormRedirectUrl}
              __cssFor={{ root: pageLinkCss }}
            >
              Complete form
            </Anchor>
          </span>,
          Toast.statusColor.danger,
        );
        return;
      }

      setIsLoadingRelevantForms(true);

      const publishedFormResponse = await publishContentPromotionForm();
      if ("error" in publishedFormResponse)
        throw new Error(publishedFormResponse.error);

      fetchContentPromotionForms();

      displayToast(
        "The config was published successfully!",
        Toast.statusColor.success,
      );
    } catch (error) {
      console.error("Error publishing content promotion form", error);
    } finally {
      setIsLoadingRelevantForms(false);
    }
  };

  const displayToast = (message: string | JSX.Element, statusColor: string) => {
    globalToasterRef.current.displayToast({
      children: message,
      statusColor,
      showIcon: true,
    });
  };

  return (
    <div css={promotedContentContainerCss}>
      <YourContentNav selector={selector} />
      <div css={innerPageContainerCss(false)}>
        {isLoadingRelevantForms ? (
          <LoadingSpinner />
        ) : (
          <>
            {relevantForms && relevantForms?.length > 0 ? (
              <>
                <ContentPreviewContainer
                  mostSeniorUserRole={mostSeniorUserRole}
                  venue={venue}
                  isLoadingFormResponses={isLoadingFormResponses}
                  formResponses={selectedFormResponses}
                  title={generatePageTitle({ selectedForm, venue })}
                  subtitle={PREVIEW_SUBTITLE}
                  selectedFormId={selectedForm?.id}
                  renderSectionBelowTitle={
                    <ContentPromotionFormLinks
                      links={generateFormLinks(relevantForms)}
                    />
                  }
                />
                {selectedForm && (
                  <BannerToPublishContentPromotionForms
                    gradeRangeText={getGradeRangeTextForSelectedForm(
                      selectedForm,
                    )}
                    handleSelectionToPublishContentPromotionForm={
                      handleSelectionToPublishContentPromotionForm as () => Promise<void>
                    }
                    isFormPublished={selectedForm.is_published}
                    isVisible={isBannerVisibleToPublishForm}
                    selectedFormRedirectUrl={selectedFormRedirectUrl}
                    subjectProductLabel={getSubjectProductLabelForSelectedForm(
                      selectedForm,
                    )}
                  />
                )}
              </>
            ) : (
              <NoContentReturned selector={selector} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PromotedAtYourVenuePage;
